/**
 * Yield to main thread to allow browser to process user interactions
 * Uses scheduler.yield() if available, falls back to MessageChannel
 */
export const yieldToMain = (): Promise<void> => {
    const scheduler = (window as any).scheduler;
    if (scheduler && typeof scheduler.yield === 'function') {
        return scheduler.yield();
    }
    // Fallback for browsers without scheduler.yield()
    return new Promise((resolve) => {
        // Prefer MessageChannel when available (faster than setTimeout(0))
        const MessageChannelCtor: typeof MessageChannel | undefined =
            (window as any).MessageChannel ?? (typeof MessageChannel !== 'undefined'
                ? MessageChannel
                : undefined);

        if (MessageChannelCtor) {
            const channel = new MessageChannelCtor();
            channel.port1.onmessage = () => {
                channel.port1.onmessage = null;
                channel.port1.close();
                channel.port2.close();
                resolve();
            };
            channel.port2.postMessage(undefined);
            return;
        }

        // Very old environments: gracefully fall back to setTimeout(0)
        setTimeout(resolve, 0);
    });
};

/**
 * Execute tasks with yield between each task
 * @param tasks Array of tasks to execute
 * @param options Configuration options
 */
export const executeWithYield = async <T>(
    tasks: Array<() => T | Promise<T>>,
    options: {
        yieldBetween?: boolean; // Whether to yield between tasks
        yieldAfter?: boolean;    // Whether to yield after all tasks
    } = {}
): Promise<T[]> => {
    const { yieldBetween = true, yieldAfter = false } = options;
    const results: T[] = [];

    for (let i = 0; i < tasks.length; i++) {
        const result = await tasks[i]();
        results.push(result);

        // Yield between tasks (except after the last one if yieldAfter is false)
        if (yieldBetween && (i < tasks.length - 1 || yieldAfter)) {
            await yieldToMain();
        }
    }

    return results;
};
